import type { DemoReadyPayload } from "../types/demoEntryAnalysis";
import type { DemoCandleTimeframe, DemoListingObject } from "../types/demoEntryAnalysis";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import { abandonDemoEntryAnalysis } from "../services/demoEntryAnalysisApi";
import { resetDemoClaimGate } from "./demoEntryAnalysisGates";

const SESSION_KEY = "demo_entry_analysis_v1";
const JOB_TTL_MS = 45 * 60 * 1000; // align with BE job TTL ~2700s

export type DemoFunnelStep =
  | "import"
  | "wizard"
  | "generating"
  | "results";

export type DemoSessionState = {
  version: 1;
  updatedAt: number;
  step: DemoFunnelStep;
  brokerName?: string;
  cohortId?: string;
  instruments?: AvailableInstrumentRow[];
  suggestedSymbol?: string | null;
  symbol?: string;
  candleTimeframe?: DemoCandleTimeframe;
  listing?: DemoListingObject;
  jobId?: string;
  /** Short-lived code to restore poll cookie after OAuth (BE prep response). */
  handoffCode?: string;
  /** @deprecated BE no longer returns poll_token; cookie + handoff_code replace it. */
  pollToken?: string;
  claimed?: boolean;
  /**
   * Set when FE starts POST /claim for this job (success or fail).
   * Survives reload so we never re-hit /claim for the same jobId.
   */
  claimAttempted?: boolean;
  readyPayload?: DemoReadyPayload | null;
  demoProcessedCount?: number;
  demoSourceCount?: number;
  /** IANA zone from Orders import (Fill Time). Used for Databento ~48h gate. */
  importTimeZone?: string | null;
  /** Newest Fill Time from the Orders CSV, as UTC epoch ms. */
  latestFillUtcMs?: number | null;
};

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

export function readDemoSession(): DemoSessionState | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoSessionState;
    if (!parsed || parsed.version !== 1) {
      clearDemoSession();
      return null;
    }
    if (Date.now() - (parsed.updatedAt || 0) > JOB_TTL_MS) {
      clearDemoSession();
      return null;
    }
    return parsed;
  } catch {
    clearDemoSession();
    return null;
  }
}

export function writeDemoSession(patch: Partial<DemoSessionState> & { step?: DemoFunnelStep }): DemoSessionState {
  const prev = readDemoSession() ?? {
    version: 1 as const,
    updatedAt: Date.now(),
    step: "import" as DemoFunnelStep,
  };
  const next: DemoSessionState = {
    ...prev,
    ...patch,
    version: 1,
    updatedAt: Date.now(),
  };
  if (isBrowser()) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  }
  return next;
}

export function clearDemoSession(): void {
  if (!isBrowser()) return;
  sessionStorage.removeItem(SESSION_KEY);
  clearPendingAuthRedirect();
  resetDemoClaimGate();
}

/**
 * Wipe ephemeral demo state and return to import.
 * Releases the BE per-IP prep lock first so Start over is not blocked by an
 * abandoned / unreachable job the visitor can no longer open.
 */
export async function restartDemoEntryAnalysis(
  navigate: (path: string) => void = (path) => {
    if (typeof window !== "undefined") window.location.assign(path);
  }
): Promise<void> {
  const prior = readDemoSession();
  const priorJobId = prior?.jobId;
  try {
    await abandonDemoEntryAnalysis(priorJobId);
  } catch (err) {
    // Retry once without job id (still releases IP lock via cookie / IP key).
    try {
      await abandonDemoEntryAnalysis();
    } catch {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[demo-ea] abandon failed during start over", err);
      }
    }
  }
  clearDemoSession();
  navigate(DEMO_ENTRY_ANALYSIS_BASE_PATH);
}

/** Path after Clerk signup/sign-in — job secrets stay in sessionStorage, not the URL. */
export function demoClaimReturnPath(): string {
  return "/demo/entry-analysis/generating?claim=1";
}

/**
 * When an unclaimed demo job is still in sessionStorage, resume claim after auth
 * instead of landing on /dashboard → /paywall.
 */
export function getPendingDemoClaimResumePath(): string | null {
  const session = readDemoSession();
  if (!session?.jobId) return null;
  if (session.claimed || session.readyPayload) return null;
  return demoClaimReturnPath();
}

/** True when auth redirect_url points back at the demo claim step. */
export function isDemoClaimReturnPath(path: string | null | undefined): boolean {
  if (!path) return false;
  try {
    const raw = path.startsWith("http") ? new URL(path).pathname + new URL(path).search : path;
    const [pathname, search = ""] = raw.split("?");
    if (!pathname.startsWith("/demo/entry-analysis/generating")) return false;
    return new URLSearchParams(search).get("claim") === "1";
  } catch {
    return false;
  }
}

const PENDING_AUTH_REDIRECT_KEY = "tessera_pending_auth_redirect_v1";

/** Persist post-auth return path so OAuth / Clerk defaults cannot drop `redirect_url`. */
export function markPendingAuthRedirect(path: string): void {
  if (!isBrowser()) return;
  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return;
  try {
    sessionStorage.setItem(PENDING_AUTH_REDIRECT_KEY, trimmed);
  } catch {
    // ignore quota / private mode
  }
}

export function peekPendingAuthRedirect(): string | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY);
    if (!raw) return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) {
      sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

export function clearPendingAuthRedirect(): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
  } catch {
    // ignore
  }
}

export function demoSignupRedirectUrl(): string {
  markPendingAuthRedirect(demoClaimReturnPath());
  return `/auth/signup?redirect_url=${encodeURIComponent(demoClaimReturnPath())}`;
}

export function demoSigninRedirectUrl(): string {
  markPendingAuthRedirect(demoClaimReturnPath());
  return `/auth/signin?redirect_url=${encodeURIComponent(demoClaimReturnPath())}`;
}

export const DEMO_ENTRY_ANALYSIS_BASE_PATH = "/demo/entry-analysis";

export function isDemoEntryAnalysisRoute(pathname: string): boolean {
  const path = pathname.trim() || "/";
  return path === DEMO_ENTRY_ANALYSIS_BASE_PATH || path.startsWith(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/`);
}
